import { useReactiveVar } from '@apollo/client'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import React, { ReactChild, ReactFragment } from 'react'

export interface ResponsiveBreakpointContainerProps {
    xs?: React.ReactNode
    sm?: React.ReactNode
    md?: React.ReactNode
    lg?: React.ReactNode
    xl?: React.ReactNode
    custom?: React.ReactNode
}

/**
 * This HOC displays a given child component based on
 * the current matching breakpoint.
 *
 * It follows mobile-first principle, so a component
 * that is rendered for the `xs` breakpoint gets rendered
 * for all other breakpoints above `xs` unless otherwise
 * specified by the props values.
 *
 * To render no component for a specific breakpoint,
 * pass `null` as value for that breakpoint prop.
 *
 */
const ResponsiveBreakpointContainer: React.FC<ResponsiveBreakpointContainerProps> = ({
    xs,
    sm,
    md,
    lg,
    xl,
    custom,
}) => {
    const theme = useTheme()
    const xsUp = useMediaQuery(theme.breakpoints.up('xs'))
    const smUp = useMediaQuery(theme.breakpoints.up('sm'))
    const mdUp = useMediaQuery(theme.breakpoints.up('md'))
    const lgUp = useMediaQuery(theme.breakpoints.up('lg'))
    const xlUp = useMediaQuery(theme.breakpoints.up('xl'))
    const { isPublishingAuthor, isAdmin, isReviewer } = useReactiveVar(userInfo)

    const customBetween = useMediaQuery(
        theme.breakpoints.between(
            'xs',
            isPublishingAuthor || isAdmin || isReviewer ? 1890 : 1550
        )
    )

    let content: boolean | ReactChild | ReactFragment | null = null
    if (xlUp && xl !== undefined) content = xl
    else if (customBetween && custom !== undefined) content = custom
    else if (lgUp && lg !== undefined) content = lg
    else if (mdUp && md !== undefined) content = md
    else if (smUp && sm !== undefined) content = sm
    else if (xsUp && xs !== undefined) content = xs

    return <>{content}</>
}

export default ResponsiveBreakpointContainer
